<script>
import api from '@utils/api'
import GoodsCharacteristicDelete from './goods-characteristic-delete'
import GoodsCharacteristicItem from './goods-characteristic-item'
import GoodsCharacteristicItemNew from './goods-characteristic-item-new'
import userHasRolesToWrite from '@utils/userPermissions'

export default {
  name: 'GoodsCharacteristic',
  components: {
    GoodsCharacteristicDelete,
    GoodsCharacteristicItem,
    GoodsCharacteristicItemNew,
  },
  props: {
    characteristic: {
      type: Object,
      default: () => {
        return {}
      },
    },
    drumvolumeUpdated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      headers: [
        { text: 'goods', value: 'goods', sortable: false },
        { text: 'characteristic', value: 'characteristic', sortable: false },
        { text: 'value', value: 'value', sortable: false },
        { text: 'actions', value: 'actions', sortable: false },
      ],
      isLoading: false,
      rowsPerPage: [10, 25, 50],
      rolesAllowedToWrite: ['SUPERUSER', 'DICTIONARIES_OPERATOR'],
      content: this.characteristic.content,
      pagination: {
        sortBy: 'number',
        descending: true,
      },
      totalElements: this.characteristic.totalElements,
      newCharacteristic: false,
      editItem: false,
      item: {},
      newItem: false,
    }
  },
  computed: {
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.pagination.page || null,
        sort_property: this.pagination.sortBy || null,
        sort_direction: this.pagination.descending || null,
      }
    },
    userAllowedToCreate() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
    },
    getHeaders() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
        ? this.headers
        : this.headers.slice(0, this.headers.length - 1)
    },
    filteredContent() {
      // Временная затычка
      const unusedCharacteristics = [119, 120, 121]
      return this.content.filter(
        x => !unusedCharacteristics.includes(x.characteristic)
      )
    },
  },
  watch: {
    params(val, oldVal) {
      // FIXME: Исправлено в версии 1.1 vuetify
      if (JSON.stringify(val) !== JSON.stringify(oldVal)) this.fetchData(val)
    },
    drumvolumeUpdated(value) {
      if (value) {
        this.fetchData(this.params)
        this.$emit('characteristics-updated')
      }
    },
    characteristic: {
      handler() {
        this.fetchData(this.params)
      },
      deep: true,
    },
  },
  methods: {
    fetchData(params) {
      this.isLoading = true
      const payload = {
        goodsId: this.$route.params.goodsId,
        params: params,
      }
      api.getGoodsCharacteristic(payload).then(response => {
        this.content = response.content
        this.totalElements = response.totalElements
        this.isLoading = false
      })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    openItem(item) {
      this.item = item
      this.editItem = true
    },
    closeItem() {
      this.editItem = false
      this.item = {}
    },
    newItemCreated() {
      this.newItem = false
      this.fetchData(this.params)
      this.$emit('characteristics-updated')
    },
    characteristicDeleted() {
      this.fetchData(this.params)
      this.$emit('characteristics-updated')
    },
    getSymbol(code) {
      switch (code) {
        case 'liters':
          return 'L'
        case 'us_gallon':
        case 'imperial_gallon':
          return 'gal'
        case 'millimeters':
          return 'mm'
        case 'inches':
          return '"'
        case 'g_to_cm':
          return 'g/cm3'
        case 'pound_gallon':
          return 'lbs/gal'
        case 'pound_to_inches':
          return 'lbs/in3'
        default:
          return ''
      }
    },
    getValueInMeasureUnits(item) {
      if (!item.value) return item.value
      if (
        item.characteristic === 120 ||
        item.characteristic === 121 ||
        (item.characteristic >= 107 && item.characteristic <= 110)
      )
        return item.value
      const measure = this.$store.getters['userSettings/getUnitsMeasurement']
      return item.characteristic === 114
        ? `${(item.value / measure.volumeMultiplier).toFixed(
            2
          )} ${this.getSymbol(measure.volumeMeasure)}`
        : `${(item.value / measure.lengthMultiplier).toFixed(
            2
          )} ${this.getSymbol(measure.lengthMeasure)}`
    },
  },
}
</script>

<template>
  <v-container fluid>
    <v-toolbar 
      flat 
      color="grey lighten-5">
      <v-spacer />
      <v-btn 
        color="info" 
        @click="fetchData(params)">
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn
        v-if="userAllowedToCreate"
        color="primary"
        dark
        @click="newItem = true"
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-toolbar>
    <v-data-table
      :headers="getHeaders"
      :items="filteredContent"
      :pagination.sync="pagination"
      :total-items="totalElements"
      :rows-per-page-items="rowsPerPage"
      :rows-per-page-text="$t('table__rows_per_page_text')"
      :loading="isLoading"
      :class="$style.sdTable"
      sort-icon="expand_less"
      must-sort
      hide-actions
    >
      <template 
        slot="headerCell" 
        slot-scope="props">
        {{ $t(`label__${props.header.text}`) }}
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <template 
        slot="items" 
        slot-scope="props">
        <tr 
          :class="$style.sdEditRow" 
          @click="openItem(props.item)">
          <td>{{ getValue('goods', props.item.goods) }}</td>
          <td>{{ getValue('characteristic', props.item.characteristic) }}</td>
          <td>{{ getValueInMeasureUnits(props.item) }}</td>
          <td v-if="userAllowedToCreate">
            <goods-characteristic-delete
              :item-id="props.item.id"
              :name="getValue('characteristic', props.item.characteristic)"
              @item-deleted="characteristicDeleted"
            />
          </td>
        </tr>
      </template>
      <template 
        slot="pageText" 
        slot-scope="{ pageStart, pageStop }">
        {{ pageStart }}-{{ pageStop }} {{ $t('table__page_of') }}
        {{ totalElements }}
      </template>
    </v-data-table>
    <goods-characteristic-item
      :dialog="editItem"
      :item="item"
      @edit-canceled="closeItem"
    />
    <goods-characteristic-item-new
      :dialog="newItem"
      :goods-id="$route.params.goodsId"
      @new-canceled="newItem = false"
      @new-item-created="newItemCreated"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
